import * as THREE from 'three';

function toNumber(value, fallback) {
  if (value === undefined || value === null || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function coerceGetShadow(value) {
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return true;
}

function normalizeShadowType(shadow) {
  if (!shadow) return 'Soft Shadow';
  const normalized = String(shadow).trim();
  if (
    normalized === 'No Shadow' ||
    normalized === 'Hard Shadow' ||
    normalized === 'Soft Shadow'
  ) {
    return normalized;
  }

  const lower = normalized.toLowerCase();
  if (lower === 'no shadow') return 'No Shadow';
  if (lower === 'hard shadow') return 'Hard Shadow';
  if (lower === 'soft shadow') return 'Soft Shadow';
  return 'Soft Shadow';
}

/** Normalize shadow-related fields after API load / publish reload. */
export function normalizeLightShadowFields(light) {
  if (!light) return light;

  const next = {
    ...light,
    getShadow: coerceGetShadow(light.getShadow ?? light.get_shadow),
    shadow: normalizeShadowType(light.shadow),
    shadowRadius: toNumber(light.shadowRadius ?? light.shadow_radius, 4),
    shadowBlur: toNumber(light.shadowBlur ?? light.shadow_blur, 3.5),
    shadowDarkness: toNumber(light.shadowDarkness ?? light.shadow_darkness, 1),
    shadowOpacity: toNumber(light.shadowOpacity ?? light.shadow_opacity, 1),
  };

  if (light.intensity !== undefined && typeof light.intensity !== 'number') {
    next.intensity = toNumber(light.intensity, 1);
  }
  if (light.range !== undefined && typeof light.range !== 'number') {
    next.range = toNumber(light.range, 100);
  }

  return next;
}

export function resolveLightTypeForShadow(light, threeLight) {
  if (light?.light_type) return light.light_type;

  switch (threeLight?.type) {
    case 'DirectionalLight':
      return 'Directional Light';
    case 'PointLight':
      return 'Point Light';
    case 'SpotLight':
      return 'Spot Light';
    case 'RectAreaLight':
      return 'Area Light';
    case 'AmbientLight':
      return 'Ambient Light';
    default:
      return 'Directional Light';
  }
}

function isCastingShadowLight(light) {
  if (!light || light.getShadow === false || light.shadow === 'No Shadow') {
    return false;
  }
  const lightType = light.light_type;
  return lightType !== 'Area Light' && lightType !== 'Ambient Light';
}

/** Pick one renderer shadow-map filter from all scene lights (avoid per-light overrides). */
export function resolveRendererShadowMapType(lightData) {
  if (!Array.isArray(lightData) || lightData.length === 0) {
    return THREE.PCFSoftShadowMap;
  }

  const normalized = lightData.map((light) => normalizeLightShadowFields(light));
  let hasHard = false;
  let hasSoft = false;

  normalized.forEach((light) => {
    if (!isCastingShadowLight(light)) return;
    if (light.shadow === 'Hard Shadow') {
      hasHard = true;
    } else {
      hasSoft = true;
    }
  });

  // Soft shadows use PCFShadowMap + light.shadow.radius (matches pre-fix behavior).
  // PCFSoftShadowMap changes penumbra character and made soft shadows look wrong.
  if (hasSoft || (!hasHard && !hasSoft)) {
    return THREE.PCFShadowMap;
  }
  return THREE.PCFShadowMap;
}

export function applyRendererShadowMapType(renderer, lightData) {
  if (!renderer?.shadowMap) return;

  renderer.shadowMap.enabled = true;
  const nextType = resolveRendererShadowMapType(lightData);
  if (renderer.shadowMap.type !== nextType) {
    renderer.shadowMap.type = nextType;
    renderer.shadowMap.needsUpdate = true;
  }
}

function resolveShadowBias(lightType, isHardShadow) {
  if (isHardShadow) {
    return { bias: -0.00005, normalBias: 0.03 };
  }

  // Keep soft-shadow bias mild so penumbra/radius controls stay visible.
  switch (lightType) {
    case 'Directional Light':
      return { bias: -0.0002, normalBias: 0.02 };
    case 'Spot Light':
      return { bias: -0.0002, normalBias: 0.02 };
    case 'Point Light':
      return { bias: -0.0002, normalBias: 0.03 };
    default:
      return { bias: -0.0002, normalBias: 0.02 };
  }
}

function isThinFlatMesh(mesh) {
  const geometry = mesh?.geometry;
  if (!geometry) return false;

  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }

  const bb = geometry.boundingBox;
  if (!bb) return false;

  const sx = bb.max.x - bb.min.x;
  const sy = bb.max.y - bb.min.y;
  const sz = bb.max.z - bb.min.z;
  const dims = [sx, sy, sz].filter((d) => d > 1e-6).sort((a, b) => a - b);

  if (dims.length < 3) return false;

  const [minDim, , maxDim] = dims;
  return minDim / maxDim < 0.08;
}

function applyThinFlatReceiverMaterialHints(mesh) {
  if (!mesh?.receiveShadow || !mesh.material || !isThinFlatMesh(mesh)) return;

  const materials = Array.isArray(mesh.material)
    ? mesh.material
    : [mesh.material];

  materials.forEach((mat) => {
    if (!mat) return;
    mat.shadowSide = THREE.FrontSide;
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = 1;
    mat.polygonOffsetUnits = 2;
  });
}

/** Apply per-light shadow settings (shared by useLight and post-load refresh). */
export function configureLightShadow(threeLight, light, lightType, renderer) {
  if (
    lightType === 'Area Light' ||
    lightType === 'Ambient Light' ||
    threeLight?.type === 'AmbientLight' ||
    threeLight?.type === 'RectAreaLight'
  ) {
    threeLight.castShadow = false;
    return;
  }

  const isShadowEnabled = light.getShadow !== false;
  if (!isShadowEnabled || light.shadow === 'No Shadow') {
    threeLight.castShadow = false;
    return;
  }

  threeLight.castShadow = true;

  if (renderer?.shadowMap) {
    renderer.shadowMap.enabled = true;
  }

  const isHardShadow = light.shadow === 'Hard Shadow';
  const isSoftShadow = light.shadow === 'Soft Shadow';
  let shadowRadius = 2;
  let shadowMapSize = 1024;

  switch (light.shadow) {
    case 'Soft Shadow':
      shadowRadius = 8;
      shadowMapSize = 2048;
      break;
    case 'Hard Shadow':
      shadowRadius = 0;
      shadowMapSize = 2048;
      break;
    default:
      shadowRadius = 4;
      shadowMapSize = 2048;
  }

  const userShadowRadius =
    typeof light.shadowRadius === 'number' ? light.shadowRadius : shadowRadius;
  const userShadowBlur =
    typeof light.shadowBlur === 'number' ? light.shadowBlur : 3.5;
  const userShadowDarkness =
    typeof light.shadowDarkness === 'number' ? light.shadowDarkness : 1;
  const userShadowOpacity =
    typeof light.shadowOpacity === 'number' ? light.shadowOpacity : 1;

  if (threeLight.shadow) {
    threeLight.shadow.radius = Math.max(
      0,
      userShadowRadius + userShadowBlur * 0.6
    );
    if (typeof threeLight.shadow.intensity === 'number') {
      threeLight.shadow.intensity = Math.max(
        0,
        Math.min(1, userShadowOpacity * userShadowDarkness)
      );
    }
  }

  const { bias, normalBias } = resolveShadowBias(lightType, isHardShadow);

  if (lightType === 'Directional Light' || lightType === 'Point Light') {
    if (lightType === 'Directional Light') {
      threeLight.shadow.mapSize.set(shadowMapSize, shadowMapSize);
      threeLight.shadow.camera.near = isHardShadow ? 0.1 : 0.5;
      const directionalFrustum = Math.max(
        15,
        Math.min(light.range ?? 40, 80)
      );
      threeLight.shadow.camera.far = Math.max(directionalFrustum * 3, 120);
      threeLight.shadow.camera.left = -directionalFrustum;
      threeLight.shadow.camera.right = directionalFrustum;
      threeLight.shadow.camera.top = directionalFrustum;
      threeLight.shadow.camera.bottom = -directionalFrustum;
    } else {
      const pointMapSize = isSoftShadow
        ? 1024
        : Math.min(shadowMapSize, 1024);
      threeLight.shadow.mapSize.set(pointMapSize, pointMapSize);
      threeLight.shadow.camera.near = isHardShadow ? 0.1 : 0.5;
      threeLight.shadow.camera.far = Math.max(light.range ?? 100, 50);
      if (isSoftShadow && threeLight.shadow) {
        threeLight.shadow.radius = Math.max(
          threeLight.shadow.radius,
          userShadowRadius + userShadowBlur + 6
        );
      }
    }
    threeLight.shadow.bias = bias;
    threeLight.shadow.normalBias = normalBias;

    if (threeLight.shadow?.camera?.updateProjectionMatrix) {
      threeLight.shadow.camera.updateProjectionMatrix();
    }
  } else if (lightType === 'Spot Light') {
    const spotMapSize = isSoftShadow ? 1536 : shadowMapSize;
    threeLight.shadow.mapSize.set(spotMapSize, spotMapSize);
    threeLight.shadow.camera.near = 0.1;
    threeLight.shadow.camera.far =
      light.range === 0 ? 0.2 : (light.range ?? 100);

    const angleInDegrees = ((light.angle || Math.PI / 3) * 180) / Math.PI;
    threeLight.shadow.camera.fov = Math.min(angleInDegrees * 2, 179.9);
    threeLight.shadow.camera.updateProjectionMatrix();

    threeLight.shadow.bias = bias;
    threeLight.shadow.normalBias = normalBias;
    if (isSoftShadow && threeLight.shadow) {
      threeLight.shadow.radius = Math.max(
        threeLight.shadow.radius,
        userShadowRadius + userShadowBlur + 5
      );
    }
    if (threeLight.shadow?.camera?.updateProjectionMatrix) {
      threeLight.shadow.camera.updateProjectionMatrix();
    }
  }

  if (threeLight.shadow) {
    threeLight.shadow.needsUpdate = true;
  }
}

const LIGHT_HELPER_TYPES = new Set([
  'DirectionalLightHelper',
  'PointLightHelper',
  'SpotLightHelper',
  'AmbientLightHelper',
  'AreaLightHelper',
]);

export function isLightHelperRelated(obj) {
  let current = obj;
  while (current) {
    if (
      LIGHT_HELPER_TYPES.has(current.type) ||
      (typeof current.name === 'string' && current.name.endsWith('-helper'))
    ) {
      return true;
    }
    current = current.parent;
  }
  return false;
}

export function disableLightHelperShadows(helper) {
  if (!helper) return;

  if (helper.isMesh) {
    helper.castShadow = false;
    helper.receiveShadow = false;
  }

  helper.traverse?.((child) => {
    if (child.isMesh) {
      child.castShadow = false;
      child.receiveShadow = false;
    }
  });
}

export function disableAllSceneLightHelperShadows(scene) {
  if (!scene) return;

  scene.traverse((obj) => {
    if (
      LIGHT_HELPER_TYPES.has(obj.type) ||
      (typeof obj.name === 'string' && obj.name.endsWith('-helper'))
    ) {
      disableLightHelperShadows(obj);
    }
  });
}

export function isTransformControlsRelated(obj) {
  let current = obj;
  while (current) {
    if (
      current.isTransformControls ||
      current.isTransformControlsGizmo ||
      current.isTransformControlsPlane ||
      current.name === 'transformControls'
    ) {
      return true;
    }
    current = current.parent;
  }
  return false;
}

export function disableTransformControlsShadows(root) {
  if (!root?.traverse) return;

  root.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = false;
      child.receiveShadow = false;
    }
  });
}

function applyMeshShadowSettingsToObject(obj) {
  if (
    !obj.isMesh ||
    !obj.visible ||
    isTransformControlsRelated(obj) ||
    isLightHelperRelated(obj)
  ) {
    return;
  }

  const settings = obj.userData?.material_settings;

  if (settings?.receiveShadow !== false && !obj.receiveShadow) {
    obj.receiveShadow = true;
  }

    if (obj.receiveShadow) {
      applyThinFlatReceiverMaterialHints(obj);
    }

  if (isThinFlatMesh(obj) && settings?.castShadow !== true) {
    obj.castShadow = false;
  } else if (settings?.castShadow !== false && !obj.castShadow) {
    obj.castShadow = true;
  }
}

export function applyMeshShadowSettings(root) {
  if (!root?.traverse) return;
  root.traverse(applyMeshShadowSettingsToObject);
}

export function ensureSceneMeshesCastShadow(scene) {
  if (!scene) return;
  scene.traverse(applyMeshShadowSettingsToObject);
}

export function refreshRendererShadowMap(renderer) {
  if (!renderer?.shadowMap) return;

  renderer.shadowMap.enabled = true;
  renderer.shadowMap.needsUpdate = true;
  if (!renderer.shadowMap.autoUpdate) {
    renderer.shadowMap.autoUpdate = true;
  }
}

/** Re-apply shadow settings to all scene lights after publish / content reload. */
export function refreshAllLightShadows(scene, renderer, lightData) {
  if (!scene || !renderer?.shadowMap || !Array.isArray(lightData)) return;

  const normalizedLights = lightData.map((light) =>
    normalizeLightShadowFields(light)
  );

  applyRendererShadowMapType(renderer, normalizedLights);

  normalizedLights.forEach((light) => {
    const lightId = light.id || light._id;
    if (!lightId) return;

    const threeLight = scene.getObjectByName(lightId);
    if (!threeLight?.isLight) return;

    const lightType = resolveLightTypeForShadow(light, threeLight);
    configureLightShadow(threeLight, light, lightType, renderer);
  });

  ensureSceneMeshesCastShadow(scene);

  const transformControls = scene.getObjectByName('transformControls');
  if (transformControls) {
    disableTransformControlsShadows(transformControls);
  }

  disableAllSceneLightHelperShadows(scene);

  refreshRendererShadowMap(renderer);
}

export const SCENE_LIGHTS_CLEARED_EVENT = 'sceneLightsCleared';
export const REFRESH_SCENE_SHADOWS_EVENT = 'refreshSceneShadows';
